import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Lock, Save, X, Info } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/kit";
import api, { apiError } from "@/lib/api";

const TabRow = ({ t, locked, onChange, onRemove, anyTab }) => (
  <div data-testid={`tpl-tab-${t.name || "new"}`} className="mk-card space-y-3 p-4">
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex min-w-[220px] flex-1 items-center gap-2">
        {locked && <Lock className="h-3.5 w-3.5 text-thblue" title="Read by the dashboard — name locked" />}
        <input data-testid="tpl-tab-name" value={t.name} disabled={locked} onChange={(e) => onChange({ ...t, name: e.target.value })} placeholder="Tab name (max 31 chars)" maxLength={31} className="mk-input !py-1.5 text-sm font-semibold disabled:opacity-70" />
      </div>
      <label className="flex items-center gap-2 text-xs text-thbody">
        <Switch data-testid="tpl-tab-required" checked={!!t.required} onCheckedChange={(v) => onChange({ ...t, required: v })} /> Required
      </label>
      <label className="flex items-center gap-2 text-xs text-thbody" title="Skip the month-date check for this tab (e.g. reference tables)">
        <Switch data-testid="tpl-tab-exempt" checked={!!t.exempt} onCheckedChange={(v) => onChange({ ...t, exempt: v })} /> Skip date check
      </label>
      {!locked && <button data-testid="tpl-tab-remove" onClick={onRemove} className="ml-auto text-thdim transition-colors hover:text-thcoral"><Trash2 className="h-4 w-4" /></button>}
    </div>
    <div>
      <p className="mk-sub mb-1">Column headers (one per line or comma separated) · {t.columns.length} columns{t.extra_rows?.length ? ` · ${t.extra_rows.length} fixed header row(s) preserved` : ""}</p>
      <textarea data-testid="tpl-tab-columns" rows={Math.min(6, Math.max(2, Math.ceil(t.columns.length / 4)))} value={t.columns.join("\n")}
        onChange={(e) => onChange({ ...t, columns: e.target.value.split(/\n|,/).map((c) => c.trimStart()) })}
        className="mk-input font-mono-hud !text-[11px]" placeholder="Date&#10;Agent&#10;Metric&#10;Value" />
    </div>
  </div>
);

export const TemplateEditor = ({ module, moduleName, onClose, onSaved }) => {
  const [spec, setSpec] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/uploads/${module}/spec`).then((r) => setSpec(r.data)).catch((e) => toast.error(apiError(e))); }, [module]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { any_tab: spec.any_tab, required_any: spec.required_any || [], tabs: spec.tabs.map((t) => ({ ...t, columns: t.columns.map((c) => c.trim()).filter(Boolean) })) };
      const { data } = await api.put(`/uploads/${module}/spec`, body);
      setSpec(data);
      toast.success("Template format saved — downloads and validation now use it");
      onSaved?.(data);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  if (!spec) return <div className="mk-skel h-40" />;
  const locked = new Set(spec.locked_tabs || []);
  const upd = (i, t) => setSpec({ ...spec, tabs: spec.tabs.map((x, j) => (j === i ? t : x)) });

  return (
    <div data-testid="template-editor" className="mk-fade space-y-4 rounded-2xl border border-thcyan/25 bg-thcyan/[0.03] p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-heading text-lg font-bold text-thstrong">Template format · {moduleName}</p>
          <p className="mt-1 max-w-2xl text-xs text-thmute">Defines the tabs and column headers in the downloadable template, which tabs are required on upload, and which tabs skip the month-date check.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="tpl-cancel" onClick={onClose} className="mk-pill"><X className="h-3.5 w-3.5" /> Close</button>
          <button data-testid="tpl-save" onClick={save} disabled={busy} className="mk-btn-primary"><Save className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save format"}</button>
        </div>
      </div>
      {locked.size > 0 && (
        <p className="flex items-start gap-2 rounded-xl border border-thblue/25 bg-thblue/8 px-3 py-2 text-xs text-thblue" data-testid="tpl-locked-hint">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Tabs marked with a lock are parsed by the {moduleName} dashboard. Their names stay fixed; you can still edit their columns or add extra tabs.
        </p>
      )}
      <label className="flex items-center gap-3 text-sm text-thbody">
        <Switch data-testid="tpl-any-tab" checked={!!spec.any_tab} onCheckedChange={(v) => setSpec({ ...spec, any_tab: v })} />
        Accept any tab names on upload <Badge tone={spec.any_tab ? "good" : "neutral"}>{spec.any_tab ? "flexible" : "strict — only listed tabs are kept"}</Badge>
      </label>
      <div className="space-y-3" data-testid="tpl-tabs">
        {spec.tabs.map((t, i) => (
          <TabRow key={i} t={t} anyTab={spec.any_tab} locked={locked.has(t.name)} onChange={(nt) => upd(i, nt)} onRemove={() => setSpec({ ...spec, tabs: spec.tabs.filter((_, j) => j !== i) })} />
        ))}
      </div>
      <button data-testid="tpl-add-tab" onClick={() => setSpec({ ...spec, tabs: [...spec.tabs, { name: "", required: false, exempt: true, columns: [], extra_rows: [] }] })} className="mk-btn-ghost"><Plus className="h-3.5 w-3.5" /> Add tab</button>
    </div>
  );
};
